import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';

import {
  CreatePractitionerDocument,
  GetMessagePharmacistDocument,
  GetSettingsPractitionersDocument,
  SetPractitionerRoleDocument,
} from '~/graphql/generated/graphql';
import {
  completeRegistrationModalState,
  pageInfoState,
} from '~/state/settings_practitioners/atoms';

import { Fields } from '../PractitionerForm/types';

export const useCreatePractitioner = () => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pageInfo = useAtomValue(pageInfoState);
  const setRegistrationModal = useSetAtom(completeRegistrationModalState);

  const [setRole] = useMutation(SetPractitionerRoleDocument, {
    refetchQueries: [{ query: GetMessagePharmacistDocument }],
  });
  const [createPractitioner] = useMutation(CreatePractitionerDocument, {
    // 戻り値のpractitonerCodeをキャッシュしないように、入れている
    fetchPolicy: 'no-cache',
  });
  const [getPractitioners] = useLazyQuery(GetSettingsPractitionersDocument, {
    fetchPolicy: 'network-only',
  });

  const create = useCallback(
    async (values: Fields, organizationId: string) => {
      if (!values.username) return;

      try {
        setError(null);
        setCreating(true);

        const result = await createPractitioner({
          variables: {
            input: {
              organizationId: organizationId,
              familyName: values.familyName,
              givenName: values.givenName,
              phoneticFamilyName: values.phoneticFamilyName,
              phoneticGivenName: values.phoneticGivenName,
              username: values.username,
            },
          },
        });

        const practitionerId = result.data?.createPractitioner?.practitioner.id ?? null;
        if (practitionerId && values.roles.length > 0) {
          await setRole({
            variables: {
              input: {
                practitionerId,
                roles: values.roles,
              },
            },
          });
        }

        // スタッフ一覧更新
        getPractitioners({
          variables: {
            page: pageInfo.page,
            perPage: pageInfo.perPage,
          },
        });

        const practitionerCode = result.data?.createPractitioner?.practitionerCode ?? null;
        setRegistrationModal((_state) => ({
          ..._state,
          practitionerId,
          practitionerCode,
          isOpen: true,
        }));
        setCreating(false);
      } catch (error) {
        const message = (error && error.message) || 'エラーが発生しました';
        setError(message);
        setCreating(false);
        throw new Error(message, { cause: error });
      }
    },
    [
      createPractitioner,
      getPractitioners,
      pageInfo.page,
      pageInfo.perPage,
      setRegistrationModal,
      setRole,
    ],
  );
  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    create,
    resetError,
    creating,
    error,
  };
};
